import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { singleSpaPropsSubject } from '../../single-spa/single-spa-props';

export interface RecargaMenuCompradorOpciones {
  omitirCacheLocal?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ShellIframeService {
  /** Iframe de detalle del shell (p. ej. EscritorioComprador). */
  private static readonly IFRAME_PRINCIPAL_SELECTOR = 'iframe#ventana, iframe[name="detalle"]';

  private iframeObserver: MutationObserver | null = null;
  private readonly recargaMenuCompradorSubject = new Subject<RecargaMenuCompradorOpciones>();
  readonly recargaMenuComprador$ = this.recargaMenuCompradorSubject.asObservable();

  /**
   * Lee el `src` del iframe principal o observa el DOM hasta que aparezca.
   * @param onUrlCaptured callback con la URL absoluta del iframe.
   */
  capturarUrlSitio(onUrlCaptured: (url: string) => void): void {
    const url = this.leerUrlSitioDelIframe();
    if (url) {
      onUrlCaptured(url);
      return;
    }
    this.observarIframePrincipal(onUrlCaptured);
  }

  detenerObservacion(): void {
    this.iframeObserver?.disconnect();
    this.iframeObserver = null;
  }

  /**
   * Publica `UrlSitio` en cada clic de ítem del menú.
   * La secuencia interna garantiza que el banner reciba las props aunque la URL no cambie.
   */
  registrarNavegacionPorMenu(url: string): void {
    const trimmed = url.trim();
    if (!trimmed) {
      return;
    }
    const current = singleSpaPropsSubject.getValue();
    singleSpaPropsSubject.next({
      ...current,
      UrlSitio: trimmed,
      SecuenciaNavegacionMenu: (current.SecuenciaNavegacionMenu ?? 0) + 1
    });
  }

  /** Publica `UrlSitio` para menú, banner y demás hijos del MF. */
  actualizarUrlSitio(url: string): void {
    const trimmed = url.trim();
    if (!trimmed) {
      return;
    }
    singleSpaPropsSubject.next({
      ...singleSpaPropsSubject.getValue(),
      UrlSitio: trimmed
    });
  }

  /** Recarga el menú comprador (p. ej. tras redirección desde el MF banner). */
  solicitarRecargaMenuComprador(opciones?: RecargaMenuCompradorOpciones): void {
    this.recargaMenuCompradorSubject.next(opciones ?? {});
  }

  /** Redirección solicitada por el MF banner (mismo flujo que `menu-horizontal.redirectTo`). */
  solicitarRedireccionDesdeBanner(url: string): void {
    const trimmed = url.trim();
    if (!trimmed) {
      return;
    }
    const fullUrl = this.resolverUrlAbsoluta(trimmed);
    const redirectFn = singleSpaPropsSubject.getValue().Redirect;
    if (typeof redirectFn === 'function') {
      redirectFn(fullUrl);
    }
    this.registrarNavegacionPorMenu(fullUrl);
  }

  /** Convierte rutas relativas del menú en URL absoluta del sitio. */
  resolverUrlAbsoluta(path: string): string {
    const trimmed = path.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    const pathNorm = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${window.location.origin}${pathNorm}`;
  }

  leerUrlSitioDelIframe(): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const iframe = document.querySelector<HTMLIFrameElement>(
      ShellIframeService.IFRAME_PRINCIPAL_SELECTOR
    );
    if (!iframe) {
      return null;
    }

    const srcAttr = iframe.getAttribute('src')?.trim();
    const srcResolved = iframe.src?.trim();
    if (!srcResolved && !srcAttr) {
      return null;
    }

    return srcResolved || new URL(srcAttr!, window.location.href).href;
  }

  private observarIframePrincipal(onUrlCaptured: (url: string) => void): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    this.detenerObservacion();
    this.iframeObserver = new MutationObserver(() => {
      const url = this.leerUrlSitioDelIframe();
      if (!url) {
        return;
      }
      onUrlCaptured(url);
      this.detenerObservacion();
    });
    this.iframeObserver.observe(document.body, { childList: true, subtree: true });
  }
}
